<template>
    <section class="wrapper" :class="theme">
        <div v-if="backgroundImage" class="bg-wrapper">
            <a-picture :link="backgroundImage" :fit="backgroundFit" />
        </div>
        <s-basic-slice class="s-resources" v-bind="l10n">
            <ul class="list" :class="className">
                <li v-for="(card, i) in l10n.cards" :key="i" class="item">
                    <a-resource-card
                        v-bind="card"
                        :background="cardBackground"
                        :theme="theme"
                    />
                </li>
            </ul>
            <a-link
                v-if="l10n.link"
                class="link"
                v-bind="l10n.link"
                :type="theme === 'dark' ? 'light' : l10n.link.type"
                size="title"
                accent
                :glyph="l10n.link.glyph || 'arrow'"
            />
            <div v-if="l10n.note" class="note" v-html="l10n.note" />
        </s-basic-slice>
    </section>
</template>

<script>
import ALink from '@core/components/link/link.vue';
import APicture from '@core/components/picture/picture.vue';
import validateFit from '@core/components/picture/validateFit.js';
import AResourceCard from '@core/components/resource-card/resource-card.vue';
import contentMixin from '@core/mixins/content.js';
import l10n from '@core/mixins/l10n.js';
import styleMixin from '@core/mixins/style.js';
import SBasicSlice from '@core/slices/pages/basic-slice/basic-slice.vue';

export default {
    name: 'SResources',
    components: {
        SBasicSlice,
        AResourceCard,
        APicture,
        ALink,
    },
    mixins: [l10n, styleMixin, contentMixin],

    inheritAttrs: false,
    props: {
        cards: {
            type: Array,
            required: true,
            validator: (cards) => cards.length > 1 && cards.length < 7,
        },
        link: {
            type: Object,
            required: false,
            default: undefined,
        },
        cardBackground: {
            type: String,
            default: 'white',
        },
        backgroundImage: {
            type: [String, Object],
            default: '',
        },
        backgroundFit: {
            type: [String, Object],
            required: false,
            default: 'cover',
            validator: validateFit,
        },
        note: {
            type: String,
            required: false,
            default: '',
        },
        theme: {
            type: String,
            default: 'light',
        },
    },
    computed: {
        className() {
            return {
                'layout_short': this.l10n.cards?.length === 2 || this.l10n.cards?.length === 4,
            };
        },
    },
};
</script>

<style lang="postcss" scoped>
section.wrapper {
    position: relative;
    .bg-wrapper {
        inset: 0;
        position: absolute;
        width: 100%;
        .a-picture,
        :deep(.a-picture__img) {
            height: 100%;
            width: 100%;
        }
    }
    &.dark {
        color: var(--av-inversed-primary);
        :deep(.a-slice-header) {
            &__title, &__lead {
                color: var(--av-inversed-primary);
            }
        }
    }
}
.s-resources {
    :deep(.a-slice-header) {
        margin-bottom: 40px;
    }
    .list {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        width: 100%;
        margin-bottom: 48px;
        &.layout_short {
            @media (--viewport-desktop) {
                justify-content: space-between;
            }

            @media (--viewport-desktop-wide) {
                width: calc(((100% - 32px) / 3) * 2 + 16px);
            }
            .item {
                @media (--viewport-desktop) {
                    @mixin colls 6;
                }
            }
        }
        .item {
            @mixin colls 12;
            @media (--viewport-mobile-wide) {
                @mixin colls 6;
            }
            @media (--viewport-desktop) {
                @mixin colls 4;
            }
        }
    }
    &__item {
        @media (--viewport-mobile-wide) {
            @mixin colls 6;
        }

        @media (--viewport-desktop) {
            @mixin colls 4;
        }
    }

    .link {
        margin-bottom: 40px;

        @media (--viewport-desktop) {
            margin: 0;
            position: absolute;
            top: 8px;
            inset-inline-end: var(--container-paddings-mobile-wide);
        }

        @media (--viewport-desktop-wide) {
            inset-inline-end: var(--container-paddings-desktop-wide);
        }

        @media (--viewport-desktop-large) {
            inset-inline-end: var(--container-paddings-desktop-large);
        }
    }

    &__note {
        @mixin body;
        max-width: 212px;
        margin-top: 40px;
        a {
            text-decoration: none;
            color: var(--av-brand-secondary);
        }
        @media (--viewport-mobile-wide) {
            max-width: none;
        }
    }
}
</style>
